import React from 'react';
import { useTranslation } from 'react-i18next';
import './LetterheadPreview.scss';

const LetterheadPreview = ({
    uploadedLetterhead,
    letterheadMargins,
    onLetterheadMarginsChange
}) => {
    const { t } = useTranslation();

    if (!uploadedLetterhead) {
        return null;
    }

    // Handle both formats: preview from project service or base64 from template service
    const previewSrc = uploadedLetterhead.preview || uploadedLetterhead.base64 || uploadedLetterhead;

    // Calculate margins as percentage of A4 height (300 DPI = 3508px)
    // This ensures the preview shows the exact same proportion as the final PDF
    const A4_HEIGHT_300DPI = 3508;
    const headerPercentage = (letterheadMargins.headerHeight / A4_HEIGHT_300DPI) * 100;
    const footerPercentage = (letterheadMargins.footerHeight / A4_HEIGHT_300DPI) * 100;

    const handleDragStart = (type) => (e) => {
        e.preventDefault();
        const rect = e.currentTarget.closest('.position-relative').getBoundingClientRect();
        const startY = e.clientY;
        const containerHeight = rect.height;
        const initialValue = type === 'header' ? letterheadMargins.headerHeight : letterheadMargins.footerHeight;

        const handleDrag = (e) => {
            const deltaY = e.clientY - startY;
            const deltaPercentage = (deltaY / containerHeight) * 100;
            const deltaPixels = (deltaPercentage / 100) * A4_HEIGHT_300DPI;

            let newValue;
            if (type === 'header') {
                // For header: dragging down increases margin
                newValue = Math.max(0, Math.min(3508, initialValue + deltaPixels));
            } else {
                // For footer: dragging up increases margin (inverted logic)
                newValue = Math.max(0, Math.min(3508, initialValue - deltaPixels));
            }

            onLetterheadMarginsChange({
                ...letterheadMargins,
                [type === 'header' ? 'headerHeight' : 'footerHeight']: Math.round(newValue)
            });
        };

        const handleDragEnd = () => {
            document.removeEventListener('mousemove', handleDrag);
            document.removeEventListener('mouseup', handleDragEnd);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };

        document.addEventListener('mousemove', handleDrag);
        document.addEventListener('mouseup', handleDragEnd);
        document.body.style.cursor = 'ns-resize';
        document.body.style.userSelect = 'none';
    };

    return (
        <div className="mb-4">
            <small className="text-muted fw-medium d-block mb-2">
                {t('templates.letterhead-preview')}
            </small>
            <div className="position-relative border rounded lhp-container">
                <img
                    src={previewSrc}
                    alt={t('templates.letterhead-preview-alt')}
                    className="w-100 lhp-img"
                />

                {/* Header Margin Area */}
                <div
                    className="position-absolute border-top border-primary lhp-margin-area lhp-margin-area--header"
                    style={{ height: `${Math.min(headerPercentage, 100)}%` }}
                >
                    <small className="position-absolute top-0 start-0 text-primary fw-bold ps-2 pt-1 lhp-margin-label">
                        {t('templates.header-margin-label')}: {letterheadMargins.headerHeight}px
                    </small>

                    {/* Drag Handle for Header */}
                    <div
                        className="position-absolute d-flex align-items-center justify-content-center lhp-drag-handle lhp-drag-handle--header"
                        onMouseDown={handleDragStart('header')}
                    >
                        <div className="lhp-drag-handle__line" />
                    </div>
                </div>

                {/* Footer Margin Area */}
                <div
                    className="position-absolute border-bottom border-warning lhp-margin-area lhp-margin-area--footer"
                    style={{ height: `${Math.min(footerPercentage, 100)}%` }}
                >
                    <small className="position-absolute bottom-0 start-0 text-warning-emphasis fw-bold ps-2 pb-1 lhp-margin-label">
                        {t('templates.footer-margin-label')}: {letterheadMargins.footerHeight}px
                    </small>

                    {/* Drag Handle for Footer */}
                    <div
                        className="position-absolute d-flex align-items-center justify-content-center lhp-drag-handle lhp-drag-handle--footer"
                        onMouseDown={handleDragStart('footer')}
                    >
                        <div className="lhp-drag-handle__line" />
                    </div>
                </div>

                {/* Help Text */}
                <div className={`position-absolute text-center text-muted lhp-hint${headerPercentage < 5 && footerPercentage < 5 ? ' lhp-hint--visible' : ''}`}>
                    {t('templates.margin-drag-hint')}
                </div>
            </div>
            <small className="text-muted d-block mt-1 lhp-footer-hint">
                💡 {t('templates.margin-drag-hint')}
            </small>
        </div>
    );
};

export default LetterheadPreview;
